'use client';

import { useEffect, useState } from 'react';

import { RUNMODE } from '@/config/mode';

type BrandKey = 'avis' | 'budget' | 'budgettruck';

const BRAND_THEME: Record<BrandKey, { primary: string }> = {
  avis: { primary: '#D4002A' },
  budget: { primary: '#006BBF' },
  budgettruck: { primary: '#006BBF' },
};

const DEFAULT_BRAND: BrandKey = 'avis';

// Marker passed to the AEM error page when loaded inside the iframe.
// Prevents infinite iframe nesting if that inner render itself errors.
const SKIP_AEM_PARAM = 'error_no_iframe';

function detectBrand(host: string): BrandKey {
  const h = host.toLowerCase();
  if (h.includes('budgettruck') || h.includes('budget-truck'))
    return 'budgettruck';
  if (h.includes('budget')) return 'budget';
  return 'avis';
}

type AemState =
  | { phase: 'loading' }
  | { phase: 'iframe'; url: string }
  | { phase: 'static' };

type Props = {
  reset?: () => void;
};

export function ErrorPageContent({ reset }: Props) {
  const [brand, setBrand] = useState<BrandKey>(DEFAULT_BRAND);
  const [aem, setAem] = useState<AemState>({ phase: 'loading' });

  useEffect(() => {
    setBrand(detectBrand(globalThis.location.hostname));

    if (RUNMODE === 'author') {
      setAem({ phase: 'static' });
      return;
    }

    const params = new URLSearchParams(globalThis.location.search);
    if (params.get(SKIP_AEM_PARAM) === '1') {
      setAem({ phase: 'static' });
      return;
    }

    const controller = new AbortController();
    fetch('/api/aem-error-page', { signal: controller.signal })
      .then((r) => (r.ok ? r.json() : null))
      .then((data: null | { ok: boolean; url?: string }) => {
        // Defensive: only mount an iframe to a safe same-origin path
        // (matches the server-side check, in case the API contract drifts).
        if (data?.ok && data.url && /^\/(?!\/)/.test(data.url)) {
          setAem({ phase: 'iframe', url: data.url });
        } else {
          setAem({ phase: 'static' });
        }
        return null;
      })
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === 'AbortError')
          return;
        setAem({ phase: 'static' });
      });

    return () => controller.abort();
  }, []);

  if (aem.phase === 'iframe') {
    const separator = aem.url.includes('?') ? '&' : '?';
    return (
      <iframe
        title="Error page"
        src={`${aem.url}${separator}${SKIP_AEM_PARAM}=1`}
        style={{
          border: 'none',
          width: '100%',
          height: '100vh',
          display: 'block',
        }}
      />
    );
  }

  if (aem.phase === 'loading') {
    return <div aria-hidden style={{ minHeight: '100vh' }} />;
  }

  const theme = BRAND_THEME[brand];

  return (
    <div
      role="alert"
      style={{
        minHeight: '60vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        padding: '2rem',
        fontFamily:
          'system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
        color: '#0D0D0B',
      }}
    >
      <h1 style={{ fontSize: '1.75rem', margin: '0 0 0.75rem' }}>
        Something went wrong
      </h1>
      <p style={{ maxWidth: '36rem', margin: '0 0 1.75rem', color: '#524D4D' }}>
        We&apos;re having trouble loading this page. Please try again in a
        moment.
      </p>
      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
        {reset && (
          <button
            type="button"
            onClick={() => reset()}
            style={{
              padding: '0.6rem 1.4rem',
              borderRadius: '4px',
              border: `1px solid ${theme.primary}`,
              background: theme.primary,
              color: '#fff',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Try again
          </button>
        )}
        <a
          href="/"
          style={{
            padding: '0.6rem 1.4rem',
            borderRadius: '4px',
            border: '1px solid #0D0D0B',
            color: '#0D0D0B',
            textDecoration: 'none',
            fontWeight: 600,
          }}
        >
          Go to homepage
        </a>
      </div>
    </div>
  );
}
